/**
 * AI Answer Visibility (AEO) dashboard — the plugin's custom admin tab.
 * Plan: aapastech-backend/docs/plugins/aeo-plan.md (Phase 2).
 *
 * Reads go through TanStack Query (`qk.aeo*`, CLAUDE.md §12) and render via
 * <DataState>; the setup editor writes with `expectedVersion` and offers
 * Overwrite / Discard on a 409 instead of clobbering someone else's edit.
 *
 * Honesty rules the copy follows (plan §3/§16):
 *  - every number is a RATE over the rolling window, "measured via API";
 *  - an engine without a platform key is "Coming soon", never broken;
 *  - the Timeline sets expectations — retrieval engines move in weeks,
 *    model memory in months.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  XCircle,
  Link2,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage, conflictCurrentVersion, isVersionConflict } from '@/lib/conflict';
import { DataState } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ─── Shapes (mirror aapastech-backend/src/lib/aeo/types.ts + routes/aeo.ts) ──

type EngineId = 'perplexity' | 'chatgpt' | 'gemini' | 'claude' | 'grok';

interface Brand {
  name: string;
  aliases: string[];
  domains: string[];
  city?: string;
  country?: string;
}
interface Competitor {
  name: string;
  aliases?: string[];
  domain?: string;
}
interface Question {
  id: string;
  text: string;
  kind: 'discovery' | 'branded';
  source: 'ai' | 'template' | 'owner';
  active: boolean;
}
interface EngineScore {
  engine: EngineId;
  probes: number;
  mentionRate: number;
  citationRate: number;
  avgPosition: number | null;
}
interface ReadinessCheck {
  id: string;
  status: 'pass' | 'warn' | 'fail' | 'info';
  title: string;
  detail: string;
  fix?: string;
}
interface Summary {
  plan: string;
  limits: {
    maxQuestions: number;
    maxCompetitors: number;
    cadenceDays: number;
    monthlyProbeCap: number;
  };
  engines: Array<{ id: EngineId; label: string; inPlan: boolean; available: boolean }>;
  setup: {
    brand: Brand;
    competitors: Competitor[];
    questions: Question[];
    confirmedAt: string | null;
    createdAt?: string;
    version: number;
  } | null;
  latest: {
    score: number;
    probes: number;
    computedAt: string;
    engines: EngineScore[];
    shareOfVoice: Record<string, number>;
    topCitedDomains: Array<{ domain: string; count: number }>;
    gapQuestionIds: string[];
  } | null;
  history: Array<{ computedAt: string; score: number; probes: number }>;
  readiness: { score: number; checkedAt: string; origin: string; checks: ReadinessCheck[] } | null;
  lastRunAt: string | null;
  lastError: string | null;
  usage: { probesThisMonth: number; monthlyCap: number };
}
interface GridCell {
  engine: EngineId;
  probes: number;
  mentions: number;
  cited: number;
  latest: { ranAt: string; mentioned: boolean; cited: boolean; position: number | null } | null;
}
interface Grid {
  engines: Array<{ id: EngineId; label: string }>;
  rows: Array<{ questionId: string; question: string; cells: GridCell[] }>;
  gapContent: Record<string, { status: string; createdAt: string }>;
  windowDays?: number;
}
interface ProbeRow {
  _id: string;
  engine: EngineId;
  model?: string;
  status: 'ok' | 'error';
  error?: string;
  answer?: string;
  citations?: Array<{ url: string; title?: string }>;
  analysis?: {
    mentioned: boolean;
    cited: boolean;
    position: number | null;
    competitorsMentioned: string[];
  };
  ranAt: string;
}

interface TraceOutcome {
  before: { answers: number; mentions: number };
  after: { answers: number; mentions: number; citedSite: number; citedPost: number };
  postCitedBy: EngineId[];
}
interface ContentTrace {
  items: Array<{
    suggestionId: string;
    questionId: string;
    question: string;
    queuedAt: string;
    stage: 'waiting_for_blog' | 'dismissed' | 'draft' | 'published';
    post?: { id: string; title: string; slug: string; status: string; publishedAt?: string };
    outcome?: TraceOutcome;
  }>;
  blog: { installed: boolean; autoMode: boolean };
  perWeek: number;
}

const pct = (x: number) => `${Math.round(x * 100)}%`;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
const DAY = 86_400_000;

// ─── Page ──────────────────────────────────────────────────────────────────

export default function AeoDashboard() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const summaryQ = useQuery({
    queryKey: qk.aeoSummary(projectId),
    queryFn: async () => (await apiHelpers.aeoSummary(projectId)).data.data as Summary,
    enabled: Boolean(projectId),
  });
  const gridQ = useQuery({
    queryKey: qk.aeoGrid(projectId),
    queryFn: async () => (await apiHelpers.aeoGrid(projectId)).data.data as Grid,
    enabled: Boolean(projectId),
  });

  const contentQ = useQuery({
    queryKey: qk.aeoContent(projectId),
    queryFn: async () => (await apiHelpers.aeoContent(projectId)).data.data as ContentTrace,
    enabled: Boolean(projectId),
  });

  const run = useMutation({
    mutationFn: async () =>
      (await apiHelpers.aeoRunNow(projectId)).data.data as { message: string },
    onSuccess: (d) => {
      // The server says what the run did — never "0 checked" without a reason (§12l).
      toast.success(d.message);
      queryClient.invalidateQueries({ queryKey: qk.aeo(projectId) });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Run failed')),
  });

  const [drill, setDrill] = useState<{
    questionId: string;
    question: string;
    engine: EngineId;
    label: string;
  } | null>(null);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/plugins/aeo`)}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Plugin
          </Button>
          <div>
            <h1 className="text-xl font-semibold">AI Answer Visibility</h1>
            <p className="text-muted-foreground text-sm">
              Do AI assistants like ChatGPT, Gemini and Claude recommend you? Measured via each
              engine&apos;s official API, sampled on a schedule, reported as rates.
            </p>
          </div>
        </div>
        <Button onClick={() => run.mutate()} disabled={run.isPending}>
          {run.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Run now
        </Button>
      </div>

      <DataState query={summaryQ}>
        {(s) => (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <ScoreCard s={s} />
              <UsageCard s={s} />
              <TimelineCard s={s} />
            </div>
            {s.lastError && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Last run had a problem (retried automatically):{' '}
                  <span className="font-mono text-xs">{s.lastError}</span>
                </span>
              </div>
            )}
            <EnginesCard s={s} />
            <Section
              title="Questions × engines"
              hint="Click a cell to read what the AI actually said, with its sources."
            >
              <DataState query={gridQ}>
                {(g) => (
                  <QuestionGrid
                    grid={g}
                    gapIds={new Set(s.latest?.gapQuestionIds ?? [])}
                    plan={s.plan}
                    onOpen={(row, cell, label) =>
                      setDrill({
                        questionId: row.questionId,
                        question: row.question,
                        engine: cell.engine,
                        label,
                      })
                    }
                  />
                )}
              </DataState>
            </Section>
            <Section
              title="Content sent to your Blog plugin"
              hint="This plugin never writes posts. It sends each missed question to your Blog plugin with an answer-first brief, then tracks what happened."
            >
              <DataState query={contentQ}>
                {(c) => <ContentTraceList data={c} plan={s.plan} projectId={projectId} />}
              </DataState>
            </Section>
            <div className="grid gap-4 md:grid-cols-2">
              <ShareOfVoiceCard s={s} />
              <SourcesCard s={s} />
            </div>
            <ReadinessCard s={s} />
            <SetupEditor projectId={projectId} s={s} />
          </>
        )}
      </DataState>

      <ProbeDrilldown projectId={projectId} target={drill} onClose={() => setDrill(null)} />
    </div>
  );
}

// ─── Building blocks ───────────────────────────────────────────────────────

function Section({
  title,
  hint,
  children,
  right,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <section className="bg-card rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">{title}</h2>
          {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function ScoreCard({ s }: { s: Summary }) {
  const hist = s.history.slice(-12);
  return (
    <Section
      title="AI visibility score"
      hint="0–100 · mentions, citations and list position, rolling 4 weeks"
    >
      {s.latest ? (
        <div className="flex items-end gap-4">
          <div className="text-5xl font-bold tabular-nums">{s.latest.score}</div>
          {/* Last 12 runs. Single series → no legend; the title names it. */}
          <div
            className="flex h-12 flex-1 items-end gap-[2px]"
            aria-label="Score history, last 12 runs"
          >
            {hist.map((h, i) => (
              <div
                key={i}
                title={`${fmtDate(h.computedAt)}: ${h.score} (${h.probes} answers)`}
                className="bg-primary/70 hover:bg-primary min-h-[2px] flex-1 rounded-t"
                style={{ height: `${Math.max(4, h.score)}%` }}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          No answers yet. The first check runs within the hour — or press <strong>Run now</strong>.
        </p>
      )}
      <p className="text-muted-foreground mt-2 text-xs">
        {s.latest ? `${s.latest.probes} answers in the window · ` : ''}last run{' '}
        {fmtDate(s.lastRunAt)}
      </p>
    </Section>
  );
}

function UsageCard({ s }: { s: Summary }) {
  const { probesThisMonth: used, monthlyCap: cap } = s.usage;
  const ratio = cap ? Math.min(1, used / cap) : 0;
  const high = ratio >= 0.8;
  return (
    <Section
      title="Checks this month"
      hint={`${s.plan.charAt(0).toUpperCase()}${s.plan.slice(1)} plan`}
    >
      <div className="text-2xl font-semibold tabular-nums">
        {used.toLocaleString()}{' '}
        <span className="text-muted-foreground text-base font-normal">
          / {cap.toLocaleString()}
        </span>
      </div>
      <div
        className="bg-muted mt-2 h-2 overflow-hidden rounded"
        role="meter"
        aria-valuenow={used}
        aria-valuemax={cap}
      >
        <div
          className={`h-full rounded ${high ? 'bg-amber-500' : 'bg-primary'}`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
      <p className="text-muted-foreground mt-2 flex items-center gap-1 text-xs">
        {high && <AlertTriangle className="h-3 w-3 text-amber-600" />}
        {ratio >= 1
          ? 'Limit reached — checks resume next month (or upgrade).'
          : high
            ? `${pct(ratio)} used — we email you at 80%, 95% and 100%.`
            : `One check = one question asked once on one engine. Resets on the 1st.`}
      </p>
    </Section>
  );
}

const STAGES = [
  { key: 'w1', label: 'Week 1', body: 'Readiness fixes live, baseline measured.' },
  {
    key: 'w2',
    label: 'Weeks 2–6',
    body: 'Search-based engines (Perplexity, ChatGPT search, Copilot) start reflecting new pages.',
  },
  {
    key: 'm3',
    label: 'Months 3–12',
    body: 'Model memory catches up — answers given without search.',
  },
];

function TimelineCard({ s }: { s: Summary }) {
  const start = s.setup?.createdAt ? new Date(s.setup.createdAt).getTime() : Date.now();
  const days = Math.max(0, Math.floor((Date.now() - start) / DAY));
  const current = days < 7 ? 0 : days < 42 ? 1 : 2;
  return (
    <Section title="What to expect" hint={`Day ${days + 1} since setup`}>
      <ol className="space-y-2">
        {STAGES.map((st, i) => (
          <li key={st.key} className="flex gap-2 text-sm">
            <span
              className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${i < current ? 'bg-primary/50' : i === current ? 'bg-primary ring-primary/30 ring-4' : 'bg-muted'}`}
            />
            <span>
              <span className={i === current ? 'font-semibold' : 'text-muted-foreground'}>
                {st.label}
              </span>
              <span className="text-muted-foreground block text-xs">{st.body}</span>
            </span>
          </li>
        ))}
      </ol>
    </Section>
  );
}

function EnginesCard({ s }: { s: Summary }) {
  const scores = new Map((s.latest?.engines ?? []).map((e) => [e.engine, e]));
  return (
    <Section
      title="By engine"
      hint="Mentioned = named in the answer · Cited = your site among its sources"
    >
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {s.engines.map((e) => {
          const sc = scores.get(e.id);
          return (
            <div key={e.id} className="rounded-md border p-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">{e.label}</span>
                {!e.available ? (
                  <Badge variant="secondary">Coming soon</Badge>
                ) : !e.inPlan ? (
                  <Badge variant="outline">Upgrade</Badge>
                ) : null}
              </div>
              {e.available && e.inPlan ? (
                sc ? (
                  <dl className="mt-2 space-y-1 text-sm">
                    <Row k="Mentioned" v={pct(sc.mentionRate)} />
                    <Row k="Cited" v={pct(sc.citationRate)} />
                    <Row k="Avg. position" v={sc.avgPosition ? `#${sc.avgPosition}` : '—'} />
                    <Row k="Answers" v={String(sc.probes)} />
                  </dl>
                ) : (
                  <p className="text-muted-foreground mt-2 text-xs">
                    Waiting for the first answers.
                  </p>
                )
              ) : (
                <p className="text-muted-foreground mt-2 text-xs">
                  {!e.available ? 'Being connected on our side.' : 'Not included in your plan.'}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="tabular-nums">{v}</dd>
    </div>
  );
}

function QuestionGrid({
  grid,
  gapIds,
  plan,
  onOpen,
}: {
  grid: Grid;
  gapIds: Set<string>;
  plan: string;
  onOpen: (row: Grid['rows'][number], cell: GridCell, label: string) => void;
}) {
  if (!grid.rows.length) {
    return (
      <p className="text-muted-foreground text-sm">
        No active questions yet — add some in Setup below.
      </p>
    );
  }
  const proPlus = plan === 'pro' || plan === 'business';
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="text-muted-foreground border-b text-left text-xs">
            <th className="py-2 pr-2 font-medium">Question</th>
            {grid.engines.map((e) => (
              <th key={e.id} className="px-2 py-2 text-center font-medium">
                {e.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((row) => {
            const gap = gapIds.has(row.questionId);
            const content = grid.gapContent[row.questionId];
            return (
              <tr key={row.questionId} className="border-b last:border-0">
                <td className="py-2 pr-2 align-top">
                  <div>{row.question}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {gap && (
                      <Badge variant="outline" className="text-[10px]">
                        Never named
                      </Badge>
                    )}
                    {content ? (
                      <Badge variant="secondary" className="text-[10px]">
                        <FileText className="mr-1 h-3 w-3" />
                        Sent to Blog ·{' '}
                        {content.status === 'generated'
                          ? 'written'
                          : content.status === 'dismissed'
                            ? 'dismissed'
                            : 'waiting'}
                      </Badge>
                    ) : gap && !proPlus ? (
                      <span className="text-muted-foreground text-[10px]">
                        Pro sends this to your Blog plugin as a topic
                      </span>
                    ) : null}
                  </div>
                </td>
                {row.cells.map((cell) => {
                  const label =
                    grid.engines.find((e) => e.id === cell.engine)?.label ?? cell.engine;
                  return (
                    <td key={cell.engine} className="px-1 py-1 text-center align-top">
                      <button
                        type="button"
                        disabled={!cell.probes}
                        onClick={() => onOpen(row, cell, label)}
                        className="hover:bg-muted w-full rounded px-2 py-2 disabled:cursor-default disabled:hover:bg-transparent"
                        title={
                          cell.probes
                            ? `Named in ${cell.mentions} of ${cell.probes} answers · cited ${cell.cited}×`
                            : 'Not checked yet'
                        }
                      >
                        {cell.probes ? (
                          <span className="inline-flex flex-col items-center gap-0.5">
                            <span className="inline-flex items-center gap-1">
                              {cell.mentions > 0 ? (
                                <CheckCircle2
                                  className="h-4 w-4 text-emerald-600"
                                  aria-label="mentioned"
                                />
                              ) : (
                                <XCircle
                                  className="text-muted-foreground h-4 w-4"
                                  aria-label="not mentioned"
                                />
                              )}
                              <span className="tabular-nums">
                                {cell.mentions}/{cell.probes}
                              </span>
                            </span>
                            <span className="text-muted-foreground text-[10px]">
                              {cell.latest?.position ? `#${cell.latest.position}` : ''}
                              {cell.cited > 0 ? (
                                <span className="ml-1 inline-flex items-center">
                                  <Link2 className="h-3 w-3" /> cited
                                </span>
                              ) : null}
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ShareOfVoiceCard({ s }: { s: Summary }) {
  const brand = s.setup?.brand.name;
  const rows = Object.entries(s.latest?.shareOfVoice ?? {}).sort((a, b) => b[1] - a[1]);
  return (
    <Section title="Share of voice" hint="Of every business mention across answers, who got it">
      {rows.length ? (
        <ul className="space-y-2">
          {rows.map(([name, share]) => (
            <li key={name} className="text-sm" title={`${name}: ${pct(share)}`}>
              <div className="flex justify-between">
                <span className={name === brand ? 'font-semibold' : ''}>
                  {name}
                  {name === brand ? ' (you)' : ''}
                </span>
                <span className="tabular-nums">{pct(share)}</span>
              </div>
              <div className="bg-muted mt-1 h-1.5 overflow-hidden rounded">
                <div
                  className={`h-full rounded ${name === brand ? 'bg-primary' : 'bg-primary/40'}`}
                  style={{ width: `${share * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-sm">
          {s.limits.maxCompetitors
            ? 'Add competitors in Setup to compare.'
            : 'Competitor tracking starts on Starter.'}
        </p>
      )}
    </Section>
  );
}

function SourcesCard({ s }: { s: Summary }) {
  const rows = (s.latest?.topCitedDomains ?? []).slice(0, 10);
  return (
    <Section
      title="Sources AI cites for your questions"
      hint="Being listed or reviewed on these sites is what moves answers"
    >
      {rows.length ? (
        <ol className="space-y-1 text-sm">
          {rows.map((r) => (
            <li key={r.domain} className="flex justify-between">
              <a
                href={`https://${r.domain}`}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                {r.domain}
              </a>
              <span className="text-muted-foreground tabular-nums">{r.count}×</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-muted-foreground text-sm">No citations recorded yet.</p>
      )}
    </Section>
  );
}

function statusIcon(st: ReadinessCheck['status']) {
  if (st === 'pass') return <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="pass" />;
  if (st === 'fail') return <XCircle className="h-4 w-4 text-red-600" aria-label="fail" />;
  if (st === 'warn') {
    return <AlertTriangle className="h-4 w-4 text-amber-600" aria-label="warning" />;
  }
  return <Info className="h-4 w-4 text-sky-600" aria-label="info" />;
}

function ReadinessCard({ s }: { s: Summary }) {
  const r = s.readiness;
  const order = { fail: 0, warn: 1, info: 2, pass: 3 } as const;
  return (
    <Section
      title="Can AI read your site?"
      hint={
        r
          ? `${r.origin} · checked ${fmtDate(r.checkedAt)}`
          : 'Runs with the first check, then weekly'
      }
      right={r ? <span className="text-2xl font-semibold tabular-nums">{r.score}</span> : null}
    >
      {r ? (
        <ul className="divide-y">
          {[...r.checks]
            .sort((a, b) => order[a.status] - order[b.status])
            .map((c) => (
              <li key={c.id} className="flex gap-2 py-2 text-sm">
                <span className="mt-0.5">{statusIcon(c.status)}</span>
                <span>
                  <span className="font-medium">{c.title}</span>
                  <span className="text-muted-foreground block text-xs">{c.detail}</span>
                  {c.fix && c.status !== 'pass' && (
                    <span className="mt-1 block text-xs">Fix: {c.fix}</span>
                  )}
                </span>
              </li>
            ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-sm">Not audited yet.</p>
      )}
    </Section>
  );
}

// ─── Drill-down: the stored answer + citations ─────────────────────────────

function ProbeDrilldown({
  projectId,
  target,
  onClose,
}: {
  projectId: string;
  target: { questionId: string; question: string; engine: EngineId; label: string } | null;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: qk.aeoProbes(projectId, target?.questionId ?? '', target?.engine ?? ''),
    queryFn: async () =>
      (
        await apiHelpers.aeoProbes(projectId, {
          questionId: target!.questionId,
          engine: target!.engine,
          limit: 5,
        })
      ).data.data as ProbeRow[],
    enabled: Boolean(target),
  });
  return (
    <Dialog open={Boolean(target)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{target?.label}</DialogTitle>
          <DialogDescription>
            “{target?.question}” — recent answers, newest first. Answers vary run to run.
          </DialogDescription>
        </DialogHeader>
        <DataState query={q}>
          {(rows) => (
            <div className="space-y-4">
              {rows.filter((r) => r.status === 'ok').length === 0 && (
                <p className="text-muted-foreground text-sm">No answers recorded.</p>
              )}
              {rows
                .filter((r) => r.status === 'ok')
                .map((r) => (
                  <article key={r._id} className="rounded-md border p-3">
                    <div className="text-muted-foreground mb-2 flex flex-wrap items-center gap-2 text-xs">
                      <span>{new Date(r.ranAt).toLocaleString()}</span>
                      {r.model && <span className="font-mono">{r.model}</span>}
                      {r.analysis?.mentioned ? (
                        <Badge variant="secondary">
                          Named you{r.analysis.position ? ` · #${r.analysis.position}` : ''}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not named</Badge>
                      )}
                      {r.analysis?.cited && <Badge variant="secondary">Cited your site</Badge>}
                    </div>
                    <p className="whitespace-pre-wrap text-sm">{r.answer}</p>
                    {r.analysis?.competitorsMentioned?.length ? (
                      <p className="text-muted-foreground mt-2 text-xs">
                        Competitors named: {r.analysis.competitorsMentioned.join(', ')}
                      </p>
                    ) : null}
                    {r.citations?.length ? (
                      <div className="mt-2">
                        <div className="text-xs font-medium">Sources</div>
                        <ol className="list-decimal pl-5 text-xs">
                          {r.citations.map((c, i) => (
                            <li key={i} className="break-all">
                              <a
                                href={c.url}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:underline"
                              >
                                {c.title || c.url}
                              </a>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : null}
                  </article>
                ))}
            </div>
          )}
        </DataState>
      </DialogContent>
    </Dialog>
  );
}

// ─── Setup editor (PUT /setup with expectedVersion; 409 → Overwrite/Discard) ─

interface Form {
  name: string;
  aliases: string;
  domains: string;
  city: string;
  country: string;
  competitors: Competitor[];
  questions: Array<{ id?: string; text: string; kind: Question['kind']; active: boolean }>;
  version: number;
}

const splitList = (v: string) =>
  v
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

function toForm(setup: NonNullable<Summary['setup']>): Form {
  return {
    name: setup.brand.name,
    aliases: setup.brand.aliases.join(', '),
    domains: setup.brand.domains.join(', '),
    city: setup.brand.city ?? '',
    country: setup.brand.country ?? '',
    competitors: setup.competitors.map((c) => ({ ...c })),
    questions: setup.questions.map((q) => ({
      id: q.id,
      text: q.text,
      kind: q.kind,
      active: q.active,
    })),
    version: setup.version,
  };
}

function SetupEditor({ projectId, s }: { projectId: string; s: Summary }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Form | null>(s.setup ? toForm(s.setup) : null);
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState<{ serverVersion: number } | null>(null);
  const [suggested, setSuggested] = useState<Array<{
    name: string;
    domain?: string;
    mentions: number;
  }> | null>(null);

  // Hydrate from the server only while the admin has no unsaved edits —
  // a background refetch must never wipe what they are typing (§12).
  useEffect(() => {
    if (!dirty && s.setup) setForm(toForm(s.setup));
  }, [s.setup, dirty]);

  const update = (patch: Partial<Form>) => {
    setForm((f) => (f ? { ...f, ...patch } : f));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: async (opts: { force?: boolean } = {}) => {
      if (!form) return;
      const payload = {
        brand: {
          name: form.name.trim(),
          aliases: splitList(form.aliases),
          domains: splitList(form.domains),
          ...(form.city.trim() ? { city: form.city.trim() } : {}),
          ...(form.country.trim().length === 2 ? { country: form.country.trim() } : {}),
        },
        ...(s.limits.maxCompetitors > 0
          ? {
              competitors: form.competitors
                .filter((c) => c.name.trim())
                .map((c) => ({
                  name: c.name.trim(),
                  ...(c.domain?.trim() ? { domain: c.domain.trim() } : {}),
                  ...(c.aliases?.length ? { aliases: c.aliases } : {}),
                })),
            }
          : {}),
        questions: form.questions
          .filter((q) => q.text.trim())
          .map((q) => ({
            ...(q.id ? { id: q.id } : {}),
            text: q.text.trim(),
            kind: q.kind,
            active: q.active,
          })),
        ...(opts.force ? {} : { expectedVersion: form.version }),
      };
      return (await apiHelpers.aeoSaveSetup(projectId, payload)).data.data;
    },
    onSuccess: () => {
      setDirty(false);
      setConflict(null);
      queryClient.invalidateQueries({ queryKey: qk.aeo(projectId) });
      toast.success('Setup saved — new questions are checked on the next run');
    },
    onError: (err) => {
      if (isVersionConflict(err)) {
        // Keep the admin's edits; let them choose.
        setConflict({ serverVersion: conflictCurrentVersion(err) ?? form?.version ?? 0 });
        return;
      }
      toast.error(apiErrorMessage(err, 'Failed to save setup'));
    },
  });

  const suggest = useMutation({
    mutationFn: async () =>
      (await apiHelpers.aeoSuggestCompetitors(projectId)).data.data as {
        suggestions: Array<{ name: string; domain?: string; mentions: number }>;
        analysedAnswers: number;
      },
    onSuccess: (d) => {
      setSuggested(d.suggestions);
      if (!d.suggestions.length) {
        toast.info(
          d.analysedAnswers
            ? `Read ${d.analysedAnswers} answers that leave you out — no business was named often enough to suggest.`
            : 'No answers without you yet — run a check first.'
        );
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not suggest competitors')),
  });

  const activeCount = useMemo(
    () => form?.questions.filter((q) => q.active && q.text.trim()).length ?? 0,
    [form]
  );

  if (!form) {
    return (
      <Section title="Setup">
        <p className="text-muted-foreground text-sm">
          Your setup is drafted automatically from your business profile on the first run. Press{' '}
          <strong>Run now</strong>.
        </p>
      </Section>
    );
  }

  const competitorNames = new Set(form.competitors.map((c) => c.name.toLowerCase()));
  const atCompetitorCap = form.competitors.length >= s.limits.maxCompetitors;

  return (
    <Section
      title="Setup"
      hint={
        s.setup?.confirmedAt
          ? `Last saved ${fmtDate(s.setup.confirmedAt)}`
          : 'Drafted for you — review and save'
      }
      right={
        <div className="flex gap-2">
          {dirty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDirty(false);
                setConflict(null);
                if (s.setup) setForm(toForm(s.setup));
              }}
            >
              Discard changes
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => save.mutate({})}
            disabled={!dirty || save.isPending || !form.name.trim()}
          >
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save setup
          </Button>
        </div>
      }
    >
      {conflict && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          <p className="font-medium">Someone else saved this setup while you were editing.</p>
          <p className="text-xs">
            The server now has version {conflict.serverVersion} (you loaded {form.version}). Keep
            your version, or discard your edits and load theirs.
          </p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="destructive" onClick={() => save.mutate({ force: true })}>
              Overwrite with mine
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setConflict(null);
                setDirty(false);
                queryClient.invalidateQueries({ queryKey: qk.aeo(projectId) });
              }}
            >
              Discard mine
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Business name">
          <Input value={form.name} onChange={(e) => update({ name: e.target.value })} />
        </Field>
        <Field label="Other names (comma-separated)">
          <Input
            value={form.aliases}
            onChange={(e) => update({ aliases: e.target.value })}
            placeholder="Short name, old name"
          />
        </Field>
        <Field label="Your domains (comma-separated)">
          <Input
            value={form.domains}
            onChange={(e) => update({ domains: e.target.value })}
            placeholder="example.com"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City">
            <Input value={form.city} onChange={(e) => update({ city: e.target.value })} />
          </Field>
          <Field label="Country (2 letters)">
            <Input
              value={form.country}
              maxLength={2}
              onChange={(e) => update({ country: e.target.value.toUpperCase() })}
            />
          </Field>
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">
            Competitors{' '}
            <span className="text-muted-foreground font-normal">
              {form.competitors.length}/{s.limits.maxCompetitors}
            </span>
          </h3>
          {s.limits.maxCompetitors > 0 && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => suggest.mutate()}
                disabled={suggest.isPending}
              >
                {suggest.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                Suggest from AI answers
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={atCompetitorCap}
                onClick={() => update({ competitors: [...form.competitors, { name: '' }] })}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add
              </Button>
            </div>
          )}
        </div>
        {s.limits.maxCompetitors === 0 ? (
          <p className="text-muted-foreground text-sm">
            Competitor tracking starts on the Starter plan.
          </p>
        ) : (
          <>
            {suggested && suggested.length > 0 && (
              <div className="mb-3 rounded-md border border-dashed p-3">
                <p className="text-muted-foreground mb-2 text-xs">
                  Businesses AI recommended in answers that left you out. Click to add.
                </p>
                <div className="flex flex-wrap gap-2">
                  {suggested
                    .filter((c) => !competitorNames.has(c.name.toLowerCase()))
                    .map((c) => (
                      <Button
                        key={c.name}
                        size="sm"
                        variant="secondary"
                        disabled={atCompetitorCap}
                        title={`Named in ${c.mentions} answers`}
                        onClick={() =>
                          update({
                            competitors: [
                              ...form.competitors,
                              { name: c.name, ...(c.domain ? { domain: c.domain } : {}) },
                            ],
                          })
                        }
                      >
                        <Plus className="mr-1 h-3 w-3" />
                        {c.name}
                        <span className="text-muted-foreground ml-1 text-xs">{c.mentions}×</span>
                      </Button>
                    ))}
                </div>
                {atCompetitorCap && (
                  <p className="text-muted-foreground mt-2 text-xs">
                    Your plan&apos;s competitor limit is reached — remove one to add another.
                  </p>
                )}
              </div>
            )}
            <ul className="space-y-2">
              {form.competitors.map((c, i) => (
                <li key={i} className="flex gap-2">
                  <Input
                    value={c.name}
                    placeholder="Competitor name"
                    onChange={(e) =>
                      update({
                        competitors: form.competitors.map((x, j) =>
                          j === i ? { ...x, name: e.target.value } : x
                        ),
                      })
                    }
                  />
                  <Input
                    value={c.domain ?? ''}
                    placeholder="domain.com (optional)"
                    onChange={(e) =>
                      update({
                        competitors: form.competitors.map((x, j) =>
                          j === i ? { ...x, domain: e.target.value } : x
                        ),
                      })
                    }
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove competitor"
                    onClick={() =>
                      update({ competitors: form.competitors.filter((_, j) => j !== i) })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="mt-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">
            Buyer questions{' '}
            <span
              className={
                activeCount > s.limits.maxQuestions
                  ? 'text-amber-600'
                  : 'text-muted-foreground font-normal'
              }
            >
              {activeCount}/{s.limits.maxQuestions} active
            </span>
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              update({
                questions: [...form.questions, { text: '', kind: 'discovery', active: true }],
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            Add question
          </Button>
        </div>
        <p className="text-muted-foreground mb-2 text-xs">
          Discovery questions must not contain your name (they measure whether new buyers find you).
          Branded ones must. Editing a question starts its history fresh.
        </p>
        <ul className="space-y-2">
          {form.questions.map((q, i) => (
            <li key={q.id ?? `new-${i}`} className="flex items-center gap-2">
              <Switch
                checked={q.active}
                aria-label="Track this question"
                onCheckedChange={(v) =>
                  update({
                    questions: form.questions.map((x, j) => (j === i ? { ...x, active: v } : x)),
                  })
                }
              />
              <Input
                value={q.text}
                maxLength={200}
                className={q.active ? '' : 'opacity-60'}
                onChange={(e) =>
                  update({
                    questions: form.questions.map((x, j) =>
                      j === i ? { ...x, text: e.target.value } : x
                    ),
                  })
                }
              />
              <select
                value={q.kind}
                aria-label="Question type"
                className="border-input bg-background h-9 rounded-md border px-2 text-sm"
                onChange={(e) =>
                  update({
                    questions: form.questions.map((x, j) =>
                      j === i ? { ...x, kind: e.target.value as Question['kind'] } : x
                    ),
                  })
                }
              >
                <option value="discovery">Discovery</option>
                <option value="branded">Branded</option>
              </select>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove question"
                onClick={() => update({ questions: form.questions.filter((_, j) => j !== i) })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="text-muted-foreground mb-1 block text-xs">{label}</span>
      {children}
    </label>
  );
}

// ─── Content trace: topic → Blog plugin → post → AI answers after publish ──

/** Fewer answers than this after publish → say "too early", never "0%". */
const MIN_AFTER_ANSWERS = 3;

function rate(n: number, d: number) {
  return d ? `${n}/${d} (${pct(n / d)})` : '—';
}

function ContentTraceList({
  data,
  plan,
  projectId,
}: {
  data: ContentTrace;
  plan: string;
  projectId: string;
}) {
  const navigate = useNavigate();
  if (data.perWeek === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        On Pro and Business, questions AI answers without naming you are sent to your Blog plugin as
        answer-first topics (Pro 2, Business 4 a week), and tracked here. You are on{' '}
        {plan.charAt(0).toUpperCase() + plan.slice(1)}.
      </p>
    );
  }
  if (!data.blog.installed) {
    return (
      <p className="text-muted-foreground text-sm">
        Install the Blog plugin to receive topics from this plugin. Nothing is written until it is.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-xs">
        Up to {data.perWeek} topics a week.{' '}
        {data.blog.autoMode
          ? 'Your Blog plugin is on auto-mode, so it writes them on its own schedule.'
          : 'Your Blog plugin is in manual mode: generate them from Blog → Suggestions.'}
      </p>
      {data.items.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nothing sent yet — topics appear after a run finds questions where AI never names you.
        </p>
      ) : (
        <ul className="divide-y">
          {data.items.map((it) => (
            <li key={it.suggestionId} className="py-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <span className="font-medium">“{it.question}”</span>
                <Badge variant={it.stage === 'published' ? 'default' : 'secondary'}>
                  {it.stage === 'waiting_for_blog'
                    ? 'Waiting for Blog plugin'
                    : it.stage === 'dismissed'
                      ? 'Dismissed in Blog'
                      : it.stage === 'draft'
                        ? 'Written · draft'
                        : 'Published'}
                </Badge>
              </div>
              <div className="text-muted-foreground mt-1 text-xs">
                Sent {fmtDate(it.queuedAt)}
                {it.post && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="hover:underline"
                      onClick={() => navigate(`/projects/${projectId}/blog/${it.post!.id}/edit`)}
                    >
                      {it.post.title}
                    </button>
                    {it.post.publishedAt ? ` · published ${fmtDate(it.post.publishedAt)}` : ''}
                  </>
                )}
              </div>
              {it.outcome && <OutcomeLine o={it.outcome} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OutcomeLine({ o }: { o: TraceOutcome }) {
  if (o.after.answers < MIN_AFTER_ANSWERS) {
    return (
      <p className="text-muted-foreground mt-1 text-xs">
        Too early to judge: {o.after.answers} AI answer{o.after.answers === 1 ? '' : 's'} checked
        since publishing. Search-based engines usually take 2–6 weeks to pick up a new page.
      </p>
    );
  }
  return (
    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
      <div>
        <dt className="text-muted-foreground">Named you · 4 weeks before</dt>
        <dd className="tabular-nums">{rate(o.before.mentions, o.before.answers)}</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">Named you · since publishing</dt>
        <dd className="tabular-nums">{rate(o.after.mentions, o.after.answers)}</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">Cited your site</dt>
        <dd className="tabular-nums">{rate(o.after.citedSite, o.after.answers)}</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">Cited this post</dt>
        <dd className="tabular-nums">
          {o.after.citedPost}×{o.postCitedBy.length ? ` (${o.postCitedBy.join(', ')})` : ''}
        </dd>
      </div>
    </dl>
  );
}
